import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';

import StaffLoginChip from '@/components/StaffLoginChip';
import { AvatarS3PhotoKey } from '@/components/ui/Avatar/AvatarS3PhotoKey';
import { ExaCareAuth0User } from '@/hooks/useCurrentUser';
import { UserPayload } from '@/hooks/useUserQuery';
import { Role } from '@/hooks/useUserTypesQuery';
import { UserDynamicPermissions } from '@/pages/AdminHub/Staff/hooks/useDynamicPermissionQuery';
import theme from '@/theme';

const rolePriorityMapping: Record<Role, number> = {
  L0: 7, // Admin
  'L0.1': 6, // Executive Director
  L1: 5, // Manager/ Head Nurse
  L2: 4, // Nurse
  L3: 3, // Medtech
  'L3.1': 2, // Caregiver (medications enabled)
  L4: 1, // Caretaker
  L5: 0 // Support Staff
};

type PartiallyRequired<T, K extends keyof T> = Omit<T, K> &
  Required<Pick<T, K>>;

export interface UserModel
  extends UserPayload,
    PartiallyRequired<ExaCareAuth0User, 'organization_id' | 'role'> {}

export class UserModel extends UserPayload {
  private readonly enabledDynamicPermissions: Set<string>;

  constructor(user: UserPayload) {
    super(user);
    this.enabledDynamicPermissions = new Set(
      user?.enabled_dynamic_permissions || []
    );
  }

  public isAdmin = () => this.user_type?.type === 'L0';

  public hasMinimumUserRole = (role: Role): boolean =>
    rolePriorityMapping[this.user_type?.type as Role] >=
    rolePriorityMapping[role];

  public isRoleHigherOrEqualThan = (roleFrom: Role, roleTo: Role): boolean =>
    rolePriorityMapping[roleFrom] >= rolePriorityMapping[roleTo];

  public getFullName = (): string => `${this.first_name} ${this.last_name}`;

  public getInitials = (): string =>
    `${this.first_name?.charAt(0)}${this.last_name?.charAt(0)}`;

  public getPosition = (): string => this.user_type?.title ?? '';

  public getTitle = (): string => this.title ?? '';

  public getFormattedDateOfHire = (): string =>
    this.date_of_hire
      ? format(new Date(this.date_of_hire), 'MMMM d, y')
      : 'n/a';

  public getFormattedDateOfBirth = (): string =>
    this.dob ? format(parseISO(this.dob), 'MMMM d, y') : 'n/a';

  public renderFullNameCell = (currentUser: UserModel): JSX.Element => (
    <Box display="flex" alignItems="center" gap="8px">
      <AvatarS3PhotoKey
        s3PhotoKey={this.s3_photo_key}
        sx={{ height: 32, width: 32, fontSize: '1rem' }}
      >
        {this.getInitials()}
      </AvatarS3PhotoKey>
      <Stack spacing={1} sx={{ width: '150px' }}>
        {this.getFullName()}
        {!this.last_sign_in_time && currentUser.hasMinimumUserRole('L0.1') && (
          <StaffLoginChip />
        )}
      </Stack>
    </Box>
  );

  public renderDetailsCell = (): JSX.Element => (
    <Box display="flex" alignItems="center" gap="8px">
      <Box>
        <AvatarS3PhotoKey
          s3PhotoKey={this.s3_photo_key}
          sx={{ height: 32, width: 32, fontSize: '1rem' }}
        >
          {this.getInitials()}
        </AvatarS3PhotoKey>
      </Box>
      <Box display="column">
        <Box display="flex" alignItems="center" gap="8px">
          {this.getFullName()}
          <Typography variant="body2">{this.getPosition()}</Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" color={theme.palette.primary.main}>
            {this.email}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  public isSystemPermissionsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.WRITE_SYSTEM_PERMISSIONS
    ) || this.isAdmin();

  public isCRMScreenerRulesAndDataPullsPermissionsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_RULES_AND_DATA_PULLS
    );

  public isCRMScreenerGlobalRulesAndDataPullsPermissionsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_RULES_AND_DATA_PULLS_GLOBAL
    );

  public isCRMScreenerEditReviewStagePermissionsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_EDIT_REVIEW_STAGE
    );

  public isCRMScreenerClaimReferralPermissionsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_CLAIM_REFERRAL
    );

  public isCRMScreenerEditAdmissionFlags = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_EDIT_ADMISSION_FLAGS
    );

  public isCRMScreenerEditOrganizationStatesEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_MANAGE_ORGANIZATION_STATES
    );

  public isCRMScreenerManageWaitlistEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_MANAGE_WAITLIST
    );

  public isCRMScreenerSendHospitalChatMessagesEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_SEND_HOSPITAL_CHAT_MESSAGES
    );

  public isCRMScreenerOverrideAnalysisResultsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.CRM_SCREENER_OVERRIDE_ANALYSIS_RESULTS
    );

  public isEditUserTypeNotificationPreferencesEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.EDIT_USER_TYPE_NOTIFICATION_PREFERENCES
    );

  public clone = () => {
    const payload = Object.assign({}, this);
    const clonedUserModel = new UserModel(payload);
    return clonedUserModel;
  };

  public isAssessmentsEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.ASSESSMENT_REVIEW_AND_CARE_SUBSCRIPTIONS
    );

  public isDataCenterEnabled = () =>
    this.enabledDynamicPermissions.has(
      UserDynamicPermissions.DATA_CENTER_GENERAL
    );

  public isPhoneAgentEnabled = () =>
    this.enabledDynamicPermissions.has(UserDynamicPermissions.CRM_PHONE_AGENT);

  public isScreenerCustomer = () =>
    this.organization_settings?.is_screener_customer === true;

  public getSearchText = () => {
    return [this.getFullName(), this.email, this.getPosition()]
      .join(' ')
      .toLowerCase();
  };
}
